import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Pencil, Copy, Ban, Trash2, MessageSquareText, ClipboardList, Loader2, Phone, Mail, History, Download, AlertTriangle, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusSelect } from "@/components/StatusSelect";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Panel, StatusBadge, BackButton } from "@/components/common";
import { MessageDialog } from "@/components/MessageDialog";
import { NotesPanel } from "@/components/NotesPanel";
import { PhotosPanel } from "@/components/PhotosPanel";
import { BookingSummary } from "@/components/BookingSummary";
import { HistoryDialog } from "@/components/HistoryDialog";
import { useBookingActions } from "@/hooks/useBookingActions";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { CONFIRMED_STATUSES, PRICING_TYPES } from "@/lib/constants";
import { fmtDateTime, fmtDate, money } from "@/lib/format";
import { customerConfirmation, crewBrief } from "@/lib/messages";

const Row = ({ label, value, mono, testId }) => (
  <div className="grid grid-cols-[150px_1fr] gap-3 border-b border-slate-100 py-2 text-sm last:border-0">
    <dt className="text-slate-500">{label}</dt>
    <dd className={mono ? "font-mono font-semibold text-slate-900" : "whitespace-pre-wrap text-slate-900"} data-testid={testId}>{value || value === 0 ? value : "—"}</dd>
  </div>
);

const ConfirmationButton = ({ booking, onClick }) => {
  const ok = CONFIRMED_STATUSES.includes(booking.status);
  const btn = (
    <Button onClick={onClick} disabled={!ok} data-testid="btn-generate-confirmation" className="bg-[#FACC15] font-semibold text-[#0E1E38] hover:bg-[#EAB308]">
      <MessageSquareText className="h-4 w-4" /> Generate Customer Confirmation
    </Button>
  );
  if (ok) return btn;
  return (
    <TooltipProvider><Tooltip><TooltipTrigger asChild><span tabIndex={0}>{btn}</span></TooltipTrigger>
      <TooltipContent>Available once the job is Confirmed</TooltipContent></Tooltip></TooltipProvider>
  );
};

export default function BookingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [b, setB] = useState(null);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState(null);
  const actions = useBookingActions((updated) => (updated ? setB(updated) : navigate("/bookings", { replace: true })));

  useEffect(() => {
    api.get(`/bookings/${id}`).then((r) => setB(r.data)).catch((e) => setError(formatApiError(e)));
  }, [id]);

  if (error) return <div className="py-20 text-center text-slate-500" data-testid="booking-error">{error} <Link to="/bookings" className="text-sky-700 underline">Back to bookings</Link></div>;
  if (!b) return <div className="py-20 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" /></div>;

  const pricingLabel = PRICING_TYPES.find((p) => p.value === b.pricing_type)?.label;
  const hrs = b.hours_worked ?? b.estimated_hours ?? 0;
  const rate = b.pricing_type === "fixed" ? money(b.fixed_price) : b.pricing_type === "cubic" ? `${money(b.price_per_m3)} / m³ × ${b.cubic_metres || 0} m³` : `${money(b.hourly_rate)} / hr × ${Number(hrs) + Number(b.callout_hours || 0)} hrs (${b.hours_worked != null ? `${b.hours_worked} worked` : `${b.estimated_hours || 0} estimated`}${b.callout_hours ? ` + ${b.callout_hours} hr call-out` : ""})`;

  return (
    <div data-testid="booking-detail-page">
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1">
        <BackButton className="mb-0" />
        <Link to="/bookings" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="link-back-bookings">All bookings</Link>
        <Link to={`/bookings?date=${b.moving_date}`} className="inline-flex items-center gap-1 text-sm font-medium text-sky-700 hover:underline" data-testid="link-day-bookings"><CalendarDays className="h-4 w-4" /> All jobs on {fmtDate(b.moving_date, "EEE d MMM")}</Link>
      </div>
      {b.missing_info?.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="missing-info-banner">
          <AlertTriangle className="h-4 w-4" /><span className="font-semibold">Missing information:</span>
          {b.missing_info.map((f) => <span key={f} className="rounded bg-white px-2 py-0.5 text-xs font-semibold">{f}</span>)}
          <button onClick={() => actions.edit(b)} className="ml-auto text-xs font-semibold underline" data-testid="btn-fix-missing">Fix now</button>
        </div>
      )}
      <div className="mb-6 rounded-xl bg-[#0E1E38] p-5 text-white sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-sky-200/70">Move Pro Job No.</span>
              <span className="rounded bg-[#FACC15] px-2 py-0.5 font-mono text-sm font-bold text-[#0E1E38]" data-testid="detail-job-number">{b.job_number}</span>
              <StatusBadge status={b.status} testId="detail-status-badge" className={b.status === "Cancelled" ? "" : "border-white/30"} />
            </div>
            <h1 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-3xl" data-testid="detail-customer-name">{b.customer_name}</h1>
            <p className="mt-1 text-xs text-sky-100/70" data-testid="detail-last-edited">Last edited by {b.updated_by || b.created_by || "—"} at {fmtDateTime(b.updated_at)}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusSelect value={b.status} onChange={(s) => actions.setStatus(b, s)} testId="detail-status-select" className="h-9 w-[190px] border-white/20 bg-white" />
            <Button variant="secondary" size="sm" onClick={() => actions.edit(b)} data-testid="btn-edit-booking"><Pencil className="h-4 w-4" /> Edit</Button>
            <Button variant="secondary" size="sm" onClick={() => actions.duplicate(b)} data-testid="btn-duplicate-booking"><Copy className="h-4 w-4" /> Duplicate</Button>
            {b.status !== "Cancelled" && <Button variant="secondary" size="sm" onClick={() => actions.cancel(b)} data-testid="btn-cancel-booking"><Ban className="h-4 w-4" /> Cancel</Button>}
            <Button size="sm" onClick={() => actions.remove(b)} data-testid="btn-delete-booking" className="bg-rose-600 hover:bg-rose-700"><Trash2 className="h-4 w-4" /> Delete</Button>
          </div>
        </div>
        <BookingSummary b={b} />
        <div className="mt-5 flex flex-wrap gap-2 border-t border-white/10 pt-5">
          <ConfirmationButton booking={b} onClick={() => setMsg("confirmation")} />
          <Button onClick={() => setMsg("crew")} variant="outline" data-testid="btn-generate-crew-brief" className="border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white">
            <ClipboardList className="h-4 w-4" /> Copy Driver Message
          </Button>
          <Button onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/bookings/${b.id}/receipt.pdf`, "_blank")} variant="outline" data-testid="btn-download-receipt" className="border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white">
            <Download className="h-4 w-4" /> Customer Receipt (PDF)
          </Button>
          {["admin", "owner"].includes(user?.role) && (
            <Button onClick={() => setMsg("history")} variant="outline" data-testid="btn-view-history" className="border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white lg:ml-auto">
              <History className="h-4 w-4" /> View Edit History
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="grid gap-6 md:grid-cols-2">
            <Panel title="Customer">
              <dl>
                <Row label="Name" value={b.customer_name} />
                <Row label="Mobile" value={<a href={`tel:${b.mobile}`} className="inline-flex items-center gap-1 text-sky-700"><Phone className="h-3.5 w-3.5" />{b.mobile}</a>} testId="detail-mobile" />
                <Row label="Email" value={b.email && <a href={`mailto:${b.email}`} className="inline-flex items-center gap-1 text-sky-700"><Mail className="h-3.5 w-3.5" />{b.email}</a>} />
                <Row label="Emergency contact" value={[b.emergency_contact_name, b.emergency_contact_number].filter(Boolean).join(" · ")} />
              </dl>
            </Panel>
            <Panel title="Job">
              <dl>
                <Row label="Customer time" value={b.confirmation_time} />
                <Row label="State" value={b.state} />
                <Row label="Booked by" value={b.sales_person} />
                <Row label="Lead source" value={b.lead_source} />
                <Row label="Created" value={`${b.created_by || "—"} · ${fmtDateTime(b.created_at)}`} />
              </dl>
            </Panel>
          </div>
          <Panel title="Move">
            <ol className="mb-4 space-y-2 text-sm">
              <li className="flex gap-3"><span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-sky-600 text-center text-[11px] font-bold leading-5 text-white">P</span><span data-testid="detail-pickup">{b.pickup_address}</span></li>
              {b.extra_stops.map((s, i) => (
                <li key={i} className="flex gap-3"><span className="mt-0.5 h-5 w-5 shrink-0 rounded-full border-2 border-slate-300 bg-white" /><span><span className="text-xs font-medium uppercase text-slate-400">{s.type === "pickup" ? "Extra pickup" : "Extra drop-off"}</span> {s.address}</span></li>
              ))}
              <li className="flex gap-3"><span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-[#FACC15] text-center text-[11px] font-bold leading-5 text-[#0E1E38]">D</span><span data-testid="detail-delivery">{b.delivery_address}</span></li>
            </ol>
            <dl>
              <Row label="Truck size" value={b.truck_size} />
              <Row label="Bedrooms" value={b.bedrooms} />
              <Row label="Est. cubic metres" value={b.cubic_metres && `${b.cubic_metres} m³`} />
            </dl>
          </Panel>
          <Panel title="Job details – sent to the driver">
            <dl>
              <Row label="Job details" value={b.job_notes} testId="detail-job-notes" />
              <Row label="Special requirements" value={b.special_requirements} />
            </dl>
          </Panel>
          <Panel title="Internal notes – office only">
            <dl>
              <Row label="Internal notes" value={b.internal_notes} testId="detail-internal-notes" />
              <Row label="Customer-facing notes" value={b.customer_notes} />
            </dl>
          </Panel>
          <Panel title="Operations">
            <dl>
              <Row label="Driver / team" value={b.assigned_team} testId="detail-team" />
              <Row label="Removalists" value={b.men} />
              <Row label="Crew pay" testId="detail-crew-pay" value={(b.driver_payment || b.offsider1_payment || b.offsider2_payment) ? [b.driver_payment && `Driver ${money(b.driver_payment)}`, b.offsider1_payment && `${b.offsider1_name || "Offsider 1"} ${money(b.offsider1_payment)}`, b.offsider2_payment && `${b.offsider2_name || "Offsider 2"} ${money(b.offsider2_payment)}`].filter(Boolean).join(" · ") : ""} />
              <Row label="Truck assigned" value={b.truck_assigned} />
              <Row label="Bill to company" value={b.bill_to_company && <span>{b.bill_to_company}{b.invoice_number && <> · <Link to={`/invoices/${b.invoice_id}`} className="text-sky-700 underline" data-testid="detail-invoice-link">{b.invoice_number}</Link></>}{!b.invoice_number && <span className="text-xs text-amber-700"> · not yet invoiced</span>}</span>} testId="detail-bill-to" />
            </dl>
          </Panel>
        </div>
        <div className="space-y-6">
          <Panel title="Pricing & deposit">
            <dl>
              <Row label="Pricing type" value={pricingLabel} />
              <Row label="Rate" value={rate} mono />
              {b.pricing_type === "hourly" && <Row label="Hours worked" value={b.hours_worked != null ? `${b.hours_worked} hrs` : <span className="text-amber-700">Not entered yet – add when job is completed</span>} testId="detail-hours-worked" />}
              {b.callout_fee ? <Row label="Call-out fee" value={money(b.callout_fee)} mono /> : null}
              <Row label="Additional" value={`${money(b.additional_charges)}${b.additional_charges_note ? ` · ${b.additional_charges_note}` : ""}`} mono />
              <Row label="Driver extras" mono testId="detail-extras" value={
                b.driver_extras?.length ? (
                  <ul className="space-y-0.5 font-sans font-normal">
                    {b.driver_extras.map((e, i) => <li key={i} className="flex justify-between gap-2"><span>{e.type}{e.description ? ` · ${e.description}` : ""}</span><span className="font-mono font-semibold">{money(e.amount)}</span></li>)}
                    <li className="flex justify-between border-t border-slate-100 pt-0.5 font-semibold"><span>Total extras</span><span className="font-mono">{money(b.driver_extras_total)}</span></li>
                  </ul>
                ) : "None"} />
              <Row label="Total price" value={money(b.total_estimate)} mono testId="detail-total" />
              <Row label="Deposit paid" value={b.deposit_amount ? `${money(b.deposit_amount)}${b.deposit_method ? ` via ${b.deposit_method}` : ""}` : "No deposit recorded"} testId="detail-deposit" />
              <Row label="Balance paid" value={b.balance_paid_amount ? `${money(b.balance_paid_amount)}${b.balance_paid_method ? ` via ${b.balance_paid_method}` : ""}${b.balance_paid_method === "Cash" ? " – kept by driver, deducted from pay" : ""}` : "Not yet recorded"} testId="detail-balance-paid" />
            </dl>
            <div className="mt-3 flex items-center justify-between rounded-lg bg-amber-50 p-3">
              <span className="text-sm font-medium text-amber-900">Outstanding balance</span>
              <span className="font-mono text-lg font-bold text-amber-900" data-testid="detail-balance">{money(b.remaining_balance)}</span>
            </div>
          </Panel>
          <NotesPanel booking={b} onUpdated={setB} />
          <PhotosPanel booking={b} />
        </div>
      </div>

      {msg === "confirmation" && (
        <MessageDialog open onOpenChange={() => setMsg(null)} testId="confirmation-dialog" title="Customer confirmation" description="Review, edit if needed, then copy and send by SMS or WhatsApp. Nothing is sent automatically." initialText={customerConfirmation(b)} />
      )}
      {msg === "crew" && (
        <MessageDialog open onOpenChange={() => setMsg(null)} testId="crew-brief-dialog" title="Driver message" description="Short job text for the driver – copy and send to their phone." initialText={crewBrief(b)} />
      )}
      {msg === "history" && <HistoryDialog booking={b} onClose={() => setMsg(null)} />}
      {actions.dialog}
    </div>
  );
}
